// Composable Strategy Builder — sandbox core (EXPLORATORY, not shipped).
// A typed dataflow of named blocks over bar-series. This file is the thin
// vertical slice proving the model: primitives compose into named library
// blocks; a graph is evaluated bar-by-bar with state (cross needs the prior
// bar); Series vs Event are distinct types. See
// company/TickAhead-HQ/02_ENGINEERING/Composable_Strategy_Builder.md.

export interface Bar { t: number; o: number; h: number; l: number; c: number; v: number; }

/** The two value types this slice needs. Real build adds Level / Category /
 * Direction — the type system is what keeps the UI menus contextual. */
export type ValueType = "series" | "event";

export type NodeId = string;

/** A primitive is a leaf; everything else references other nodes by id. State
 * (the prior bar) lives in `crossAbove`/`crossBelow` here — the one hook that
 * later generalises to PID/Kalman without a rewrite. */
export type Node =
  | { kind: "price"; field: "o" | "h" | "l" | "c" | "v" }        // Series
  | { kind: "const"; value: number }                             // Series
  | { kind: "sma"; period: number; in: NodeId }                  // Series
  | { kind: "ema"; period: number; in: NodeId }                  // Series (stateful)
  | { kind: "rsi"; period: number; in: NodeId }                  // Series (0..100)
  | { kind: "highest"; period: number; in: NodeId }              // Series
  | { kind: "lowest"; period: number; in: NodeId }               // Series
  | { kind: "shift"; k: number; in: NodeId }                     // Series (value k bars ago)
  | { kind: "swingLowLevel"; span: number; in: NodeId }          // Series (last confirmed swing low, held)
  | { kind: "swingHighLevel"; span: number; in: NodeId }         // Series (last confirmed swing high, held)
  | { kind: "trendlineLow"; span: number }                       // Series (line through last 2 swing lows)
  | { kind: "sub"; a: NodeId; b: NodeId }                        // Series
  | { kind: "add"; a: NodeId; b: NodeId }                        // Series
  | { kind: "mul"; a: NodeId; b: NodeId }                        // Series
  | { kind: "div"; a: NodeId; b: NodeId }                        // Series
  | { kind: "stdev"; period: number; in: NodeId }                // Series (population σ over window)
  | { kind: "abs"; in: NodeId }                                  // Series
  | { kind: "maxOf"; a: NodeId; b: NodeId }                      // Series
  | { kind: "minOf"; a: NodeId; b: NodeId }                      // Series
  | { kind: "atr"; period: number }                              // Series (Wilder ATR — native, uses h/l/c)
  | { kind: "adx"; period: number }                              // Series (Wilder ADX — native, trend strength)
  | { kind: "vwap"; resetDaily: boolean }                        // Series (native, daily-reset VWAP)
  | { kind: "newsWindow"; impact: "high" | "medium" | "low"; minsBefore: number; minsAfter: number; eventKind?: "any" | "fomc" | "cpi" | "nfp" | "opex" | "earnings" | "exdiv" | "expiry" } // Event (native, from econ-calendar + earnings feed; eventKind filters the type, D-091)
  | { kind: "newsEvent"; scope: "market" | "instrument"; impact: "high" | "medium" | "low"; sentiment: "any" | "positive" | "negative"; minsBefore: number; minsAfter: number } // Event (native, from the LLM-classified news feed, D-072)
  | { kind: "timeOfDay"; tz: string; startMin: number; endMin: number } // Event (native, wall-clock time-of-day gate in an IANA zone, D-088)
  | { kind: "dayOfWeek"; tz: string; days: number }                     // Event (native, weekday bitmask — bit 0=Sun … 6=Sat — in an IANA zone, D-088)
  | { kind: "monthOfYear"; tz: string; months: number }                 // Event (native, month bitmask — bit 0=Jan … 11=Dec — in an IANA zone, D-088)
  | { kind: "gt"; a: NodeId; b: NodeId }                         // Event
  | { kind: "lt"; a: NodeId; b: NodeId }                         // Event
  | { kind: "crossAbove"; a: NodeId; b: NodeId }                 // Event (stateful)
  | { kind: "crossBelow"; a: NodeId; b: NodeId }                 // Event (stateful)
  | { kind: "and"; a: NodeId; b: NodeId }                        // Event
  | { kind: "or"; a: NodeId; b: NodeId }                         // Event
  | { kind: "not"; a: NodeId }                                   // Event
  // Structural latch (stateful, Event) — one node, two modes (D-064, Trading spec).
  // `level` is a Series giving the operative level; `ref` is frozen at the arm bar;
  // resets after firing (one-shot) or after `windowBars` elapse.
  //   mode "retest"  (break→retest): ARM on a decisive CLOSE beyond `level` on
  //     `side`; FIRE when price returns within `tol` of `ref`; also resets if the
  //     level is reclaimed (close back on the original side). side "below" = broke
  //     DOWN, retest from below (short); "above" = broke UP, retest from above (long).
  //   mode "reclaim" (sweep-and-reclaim / grab-fade): ARM on a SWEEP beyond `level`
  //     (bar low < level for "below", high > level for "above"); FIRE on a CLOSE
  //     back on the ORIGINAL side (close > ref for "below", < ref for "above").
  | { kind: "structureLatch"; level: NodeId; side: "above" | "below"; mode: "retest" | "reclaim" | "breakoutHold"; windowBars: number; tol: number; holdBars?: number } // Event (stateful)
  // Multi-timeframe: evaluate `sub` on a resampled (higher) timeframe and hold
  // its LAST CLOSED value down onto the base timeline (no repaint). `tf` in
  // minutes. This is how "4H trend up AND 15m trigger" is expressed.
  | { kind: "onTf"; tf: number; sub: Graph; out: NodeId };

export interface Graph { nodes: Record<NodeId, Node>; }

// Scheduled high-impact economic event, from the bundled econ-calendar file
// (~/.tickahead/econ_calendar.json on the box; the extension's curated tables in
// backtest). Injected into evaluate() so backtest == live read the SAME events.
export interface EconEvent { at: number; impact: "high" | "medium" | "low"; name?: string; country?: string; kind?: string; }
const IMPACT_RANK: Record<string, number> = { low: 1, medium: 2, high: 3 };

// newsWindow series: 1 on bars whose t falls within [at − minsBefore, at + minsAfter]
// of ANY event whose impact >= the node's threshold AND (when eventKind isn't "any")
// whose kind matches, else 0. Stateless per bar. eventKind lets the user drill from
// "any high-impact event" to a specific type (FOMC / CPI / NFP / earnings), D-091.
function newsWindowArr(bars: Bar[], events: EconEvent[], impact: "high" | "medium" | "low", minsBefore: number, minsAfter: number, eventKind: string = "any"): number[] {
  const threshold = IMPACT_RANK[impact] ?? 3;
  const qualifying = events.filter((e) => (IMPACT_RANK[e.impact] ?? 0) >= threshold && (eventKind === "any" || e.kind === eventKind));
  const beforeMs = minsBefore * 60_000, afterMs = minsAfter * 60_000;
  return bars.map((b) => qualifying.some((e) => b.t >= e.at - beforeMs && b.t <= e.at + afterMs) ? 1 : 0);
}

// A classified news event from the LLM feed (D-072). Injected into evaluate() so
// backtest == live read the SAME feed (like EconEvent above). `symbol` is
// pre-filtered at injection, so newsEvent nodes only filter scope/impact/sentiment.
export interface NewsEvent { at: number; scope: "market" | "instrument"; impact: "high" | "medium" | "low"; sentiment: "positive" | "negative" | "neutral"; }

// newsEvent series: 1 on bars within [at − minsBefore, at + minsAfter] of ANY fed
// event matching scope, impact >= threshold, and (when not "any") sentiment. The
// feed is empty until the backend news engine lands, so this reads all-zeros
// today — which keeps backtest == live and box parity trivially true for now.
function newsEventArr(bars: Bar[], news: NewsEvent[], scope: "market" | "instrument", impact: "high" | "medium" | "low", sentiment: "any" | "positive" | "negative", minsBefore: number, minsAfter: number): number[] {
  const threshold = IMPACT_RANK[impact] ?? 3;
  const qualifying = news.filter((e) =>
    e.scope === scope &&
    (IMPACT_RANK[e.impact] ?? 0) >= threshold &&
    (sentiment === "any" || e.sentiment === sentiment));
  const beforeMs = minsBefore * 60_000, afterMs = minsAfter * 60_000;
  return bars.map((b) => qualifying.some((e) => b.t >= e.at - beforeMs && b.t <= e.at + afterMs) ? 1 : 0);
}

// --- wall-clock timing gates (D-088) ---------------------------------------
// The ONLY timezone math in the engine. Each bar's epoch is read in the block's
// chosen IANA zone; the box VM must read the SAME IANA zone from the SAME epoch
// (IANA tz data handles DST identically on both sides), so backtest == live
// (D-064). tz is user-specified per block; defaults live in model.ts.
const WD_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
function zonedFmt(tz: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-US", { timeZone: tz, hour12: false, weekday: "short", hour: "2-digit", minute: "2-digit" });
}
// timeOfDay: 1 when the bar's local minute-of-day is inside [startMin, endMin).
// If startMin > endMin the window wraps past midnight (e.g. 22:00 → 02:00).
function timeOfDayArr(bars: Bar[], tz: string, startMin: number, endMin: number): number[] {
  const fmt = zonedFmt(tz);
  return bars.map((b) => {
    const p = fmt.formatToParts(new Date(b.t));
    const get = (t: string): number => Number(p.find((x) => x.type === t)?.value ?? "0");
    const m = (get("hour") % 24) * 60 + get("minute");
    const inside = startMin <= endMin ? (m >= startMin && m < endMin) : (m >= startMin || m < endMin);
    return inside ? 1 : 0;
  });
}
// dayOfWeek: 1 when the bar's local weekday is in the bitmask (bit 0=Sun … 6=Sat).
function dayOfWeekArr(bars: Bar[], tz: string, days: number): number[] {
  const fmt = zonedFmt(tz);
  return bars.map((b) => {
    const wd = WD_INDEX[fmt.formatToParts(new Date(b.t)).find((x) => x.type === "weekday")?.value ?? "Sun"] ?? 0;
    return (days >> wd) & 1 ? 1 : 0;
  });
}
// monthOfYear: 1 when the bar's local calendar month is in the bitmask (bit 0=Jan
// … 11=Dec). Read in the block's IANA zone so a month boundary lands on the local
// calendar, not UTC's.
function monthOfYearArr(bars: Bar[], tz: string, months: number): number[] {
  const fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, month: "numeric" });
  return bars.map((b) => {
    const m = (Number(fmt.formatToParts(new Date(b.t)).find((x) => x.type === "month")?.value ?? "1") - 1) % 12;
    return (months >> m) & 1 ? 1 : 0;
  });
}

/** Fluent builder. `b.sma(20, b.price("c"))` returns a NodeId you pass on —
 * this is how a "library block" is just a named function that emits a subgraph
 * of primitives (see lib.ts). */
export class GraphBuilder {
  readonly nodes: Record<NodeId, Node> = {};
  private n = 0;
  private add(node: Node): NodeId {
    const id = `n${this.n++}`;
    this.nodes[id] = node;
    return id;
  }
  price(field: "o" | "h" | "l" | "c" | "v"): NodeId { return this.add({ kind: "price", field }); }
  const(value: number): NodeId { return this.add({ kind: "const", value }); }
  sma(period: number, input: NodeId): NodeId { return this.add({ kind: "sma", period, in: input }); }
  ema(period: number, input: NodeId): NodeId { return this.add({ kind: "ema", period, in: input }); }
  rsi(period: number, input: NodeId): NodeId { return this.add({ kind: "rsi", period, in: input }); }
  highest(period: number, input: NodeId): NodeId { return this.add({ kind: "highest", period, in: input }); }
  lowest(period: number, input: NodeId): NodeId { return this.add({ kind: "lowest", period, in: input }); }
  shift(k: number, input: NodeId): NodeId { return this.add({ kind: "shift", k, in: input }); }
  swingLowLevel(span: number, input: NodeId): NodeId { return this.add({ kind: "swingLowLevel", span, in: input }); }
  swingHighLevel(span: number, input: NodeId): NodeId { return this.add({ kind: "swingHighLevel", span, in: input }); }
  trendlineLow(span: number): NodeId { return this.add({ kind: "trendlineLow", span }); }
  sub(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "sub", a, b }); }
  plus(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "add", a, b }); }
  mul(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "mul", a, b }); }
  div(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "div", a, b }); }
  stdev(period: number, input: NodeId): NodeId { return this.add({ kind: "stdev", period, in: input }); }
  abs(input: NodeId): NodeId { return this.add({ kind: "abs", in: input }); }
  maxOf(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "maxOf", a, b }); }
  minOf(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "minOf", a, b }); }
  atr(period: number): NodeId { return this.add({ kind: "atr", period }); }
  adx(period: number): NodeId { return this.add({ kind: "adx", period }); }
  vwap(resetDaily: boolean): NodeId { return this.add({ kind: "vwap", resetDaily }); }
  newsWindow(impact: "high" | "medium" | "low", minsBefore: number, minsAfter: number, eventKind: "any" | "fomc" | "cpi" | "nfp" | "opex" | "earnings" | "exdiv" | "expiry" = "any"): NodeId { return this.add({ kind: "newsWindow", impact, minsBefore, minsAfter, eventKind }); }
  newsEvent(scope: "market" | "instrument", impact: "high" | "medium" | "low", sentiment: "any" | "positive" | "negative", minsBefore: number, minsAfter: number): NodeId { return this.add({ kind: "newsEvent", scope, impact, sentiment, minsBefore, minsAfter }); }
  timeOfDay(tz: string, startMin: number, endMin: number): NodeId { return this.add({ kind: "timeOfDay", tz, startMin, endMin }); }
  dayOfWeek(tz: string, days: number): NodeId { return this.add({ kind: "dayOfWeek", tz, days }); }
  monthOfYear(tz: string, months: number): NodeId { return this.add({ kind: "monthOfYear", tz, months }); }
  gt(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "gt", a, b }); }
  lt(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "lt", a, b }); }
  crossAbove(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "crossAbove", a, b }); }
  crossBelow(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "crossBelow", a, b }); }
  and(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "and", a, b }); }
  or(a: NodeId, b: NodeId): NodeId { return this.add({ kind: "or", a, b }); }
  not(a: NodeId): NodeId { return this.add({ kind: "not", a }); }
  structureLatch(level: NodeId, side: "above" | "below", mode: "retest" | "reclaim" | "breakoutHold", windowBars: number, tol: number, holdBars: number = 2): NodeId {
    return this.add({ kind: "structureLatch", level, side, mode, windowBars, tol, holdBars });
  }
  /** Build a sub-strategy on a higher timeframe (`tfMinutes`), returning a node
   * usable on the base timeline. `build` gets its own scoped builder. Example:
   * `b.onTimeframe(240, h => h.gt(h.price("c"), h.sma(50, h.price("c"))))` =
   * "on 4H, close is above SMA50" — held to the base bars by the closed-bar rule. */
  onTimeframe(tfMinutes: number, build: (h: GraphBuilder) => NodeId): NodeId {
    const h = new GraphBuilder();
    const out = build(h);
    return this.add({ kind: "onTf", tf: tfMinutes, sub: h.graph(), out });
  }
  graph(): Graph { return { nodes: this.nodes }; }
}

/** Resample base bars into higher-timeframe bars (epoch-aligned buckets), and
 * for each base bar record the index of the last HTF bar that has fully CLOSED
 * as of that base bar (the current bucket is still forming, so it's excluded —
 * this is the no-repaint rule). */
export function resample(base: Bar[], tfMinutes: number): { bars: Bar[]; closedIdx: number[] } {
  const bucketMs = tfMinutes * 60_000;
  const bars: Bar[] = [];
  const formingIdx: number[] = [];
  let curBucket = NaN;
  for (let i = 0; i < base.length; i++) {
    const bar = base[i]!;
    const bkt = Math.floor(bar.t / bucketMs);
    if (bkt !== curBucket) {
      bars.push({ t: bkt * bucketMs, o: bar.o, h: bar.h, l: bar.l, c: bar.c, v: bar.v });
      curBucket = bkt;
    } else {
      const hb = bars[bars.length - 1]!;
      hb.h = Math.max(hb.h, bar.h);
      hb.l = Math.min(hb.l, bar.l);
      hb.c = bar.c;
      hb.v += bar.v;
    }
    formingIdx[i] = bars.length - 1;
  }
  const closedIdx = formingIdx.map((k) => k - 1); // last CLOSED = forming bucket − 1
  return { bars, closedIdx };
}

// --- native indicator series (whole-array, mirrors the product's chart-indicators.ts) ---
// These carry cross-bar recurrence (Wilder smoothing, cumulative VWAP) that doesn't
// fit the windowed per-bar primitives, so they're precomputed once and indexed by bar.
function atrArr(bars: Bar[], period: number): number[] {
  const out = new Array<number>(bars.length).fill(NaN);
  if (bars.length <= period || period <= 0) return out;
  const trs: number[] = [];
  for (let i = 1; i < bars.length; i++) { const c = bars[i]!, p = bars[i - 1]!; trs.push(Math.max(c.h - c.l, Math.abs(c.h - p.c), Math.abs(c.l - p.c))); }
  let atr = trs.slice(0, period).reduce((a, b) => a + b, 0) / period;
  out[period] = atr;
  for (let k = period; k < trs.length; k++) { atr = (atr * (period - 1) + trs[k]!) / period; out[k + 1] = atr; }
  return out;
}
function vwapArr(bars: Bar[], resetDaily: boolean): number[] {
  const out = new Array<number>(bars.length).fill(NaN);
  let pv = 0, vol = 0, curDay = NaN;
  for (let i = 0; i < bars.length; i++) {
    const b = bars[i]!;
    if (resetDaily) { const day = Math.floor(b.t / 86_400_000); if (day !== curDay) { curDay = day; pv = 0; vol = 0; } }
    if (b.v > 0) { const tp = (b.h + b.l + b.c) / 3; pv += tp * b.v; vol += b.v; }
    out[i] = vol > 0 ? pv / vol : NaN;
  }
  return out;
}
function adxArr(bars: Bar[], period: number): number[] {
  const out = new Array<number>(bars.length).fill(NaN);
  if (bars.length < 2 * period) return out;
  const tr: number[] = [], plusDM: number[] = [], minusDM: number[] = [];
  for (let i = 1; i < bars.length; i++) {
    const c = bars[i]!, p = bars[i - 1]!; const up = c.h - p.h, down = p.l - c.l;
    plusDM.push(up > down && up > 0 ? up : 0); minusDM.push(down > up && down > 0 ? down : 0);
    tr.push(Math.max(c.h - c.l, Math.abs(c.h - p.c), Math.abs(c.l - p.c)));
  }
  let trS = tr.slice(0, period).reduce((a, b) => a + b, 0), pS = plusDM.slice(0, period).reduce((a, b) => a + b, 0), mS = minusDM.slice(0, period).reduce((a, b) => a + b, 0);
  const dx: Array<{ idx: number; value: number }> = [];
  for (let i = period; i < tr.length; i++) {
    trS = trS - trS / period + tr[i]!; pS = pS - pS / period + plusDM[i]!; mS = mS - mS / period + minusDM[i]!;
    const plusDI = trS > 0 ? 100 * pS / trS : 0, minusDI = trS > 0 ? 100 * mS / trS : 0, denom = plusDI + minusDI;
    dx.push({ idx: i + 1, value: denom > 0 ? 100 * Math.abs(plusDI - minusDI) / denom : 0 });
  }
  if (dx.length < period) return out;
  let adx = dx.slice(0, period).reduce((a, b) => a + b.value, 0) / period;
  out[dx[period - 1]!.idx] = adx;
  for (let i = period; i < dx.length; i++) { adx = (adx * (period - 1) + dx[i]!.value) / period; out[dx[i]!.idx] = adx; }
  return out;
}

/** Topological order (inputs before dependents) so each bar computes cleanly. */
function topoOrder(graph: Graph): NodeId[] {
  const seen = new Set<NodeId>();
  const order: NodeId[] = [];
  const inputsOf = (n: Node): NodeId[] => {
    switch (n.kind) {
      case "price": case "const": case "onTf": case "trendlineLow": case "atr": case "adx": case "vwap": case "newsWindow": case "newsEvent": case "timeOfDay": case "dayOfWeek": case "monthOfYear": return []; // leaves / self-contained
      case "sma": case "ema": case "rsi": case "highest": case "lowest": case "shift": case "swingLowLevel": case "swingHighLevel": case "stdev": case "abs": return [n.in];
      case "not": return [n.a];
      case "structureLatch": return [n.level];
      default: return [(n as { a: NodeId }).a, (n as { b: NodeId }).b];
    }
  };
  const visit = (id: NodeId): void => {
    if (seen.has(id)) return;
    seen.add(id);
    for (const dep of inputsOf(graph.nodes[id]!)) visit(dep);
    order.push(id);
  };
  for (const id of Object.keys(graph.nodes)) visit(id);
  return order;
}

/** Evaluate the whole graph over the bars, returning each node's value series.
 * Series nodes yield numbers (NaN until warm); Event nodes yield 0/1. Bar-by-bar
 * with per-node history so lookbacks and cross (prior bar = state) are honest —
 * this is exactly how it would run live on the box. */
export function evaluate(graph: Graph, bars: Bar[], tfBars?: Record<number, Bar[]>, events?: EconEvent[], news?: NewsEvent[]): Record<NodeId, number[]> {
  const order = topoOrder(graph);
  const hist: Record<NodeId, number[]> = {};
  for (const id of order) hist[id] = [];
  const at = (id: NodeId, i: number): number => hist[id]![i]!;
  // Per-node mutable state for stateful primitives (swing tracking; also the
  // hook future PID/Kalman/HMM would use). Keyed by node id, opaque.
  const st: Record<NodeId, { level?: number; pts?: Array<{ i: number; v: number }>; armed?: boolean; armBar?: number; lvl?: number }> = {};

  // Precompute multi-timeframe nodes: evaluate each sub-graph on its resampled
  // bars, then hold the last CLOSED higher-TF value down onto the base timeline.
  const onTfSeries: Record<NodeId, number[]> = {};
  for (const id of order) {
    const n = graph.nodes[id]!;
    if (n.kind !== "onTf") continue;
    const real = tfBars && tfBars[n.tf];
    if (real && real.length) {
      // Evaluate on the timeframe's OWN candles (fetched), then hold the last CLOSED
      // higher/lower-TF bar onto each base bar by timestamp (no repaint).
      const sub = evaluate(n.sub, real, tfBars);
      const out = sub[n.out]!;
      const tfMs = n.tf * 60_000;
      const series = new Array<number>(bars.length);
      let j = -1;
      for (let i = 0; i < bars.length; i++) {
        const bt = bars[i]!.t;
        while (j + 1 < real.length && real[j + 1]!.t + tfMs <= bt) j++;
        series[i] = j >= 0 ? out[j]! : NaN;
      }
      onTfSeries[id] = series;
    } else {
      // No fetched candles for this TF → resample the base series (works for higher TFs).
      const { bars: htf, closedIdx } = resample(bars, n.tf);
      const sub = evaluate(n.sub, htf, tfBars);
      const out = sub[n.out]!;
      onTfSeries[id] = bars.map((_, i) => { const ci = closedIdx[i]!; return ci >= 0 ? out[ci]! : NaN; });
    }
  }

  // Precompute native indicators (whole-array recurrence) once.
  const nativeSeries: Record<NodeId, number[]> = {};
  for (const id of order) {
    const n = graph.nodes[id]!;
    if (n.kind === "atr") nativeSeries[id] = atrArr(bars, n.period);
    else if (n.kind === "adx") nativeSeries[id] = adxArr(bars, n.period);
    else if (n.kind === "vwap") nativeSeries[id] = vwapArr(bars, n.resetDaily);
    else if (n.kind === "newsWindow") nativeSeries[id] = newsWindowArr(bars, events ?? [], n.impact, n.minsBefore, n.minsAfter, n.eventKind ?? "any");
    else if (n.kind === "newsEvent") nativeSeries[id] = newsEventArr(bars, news ?? [], n.scope, n.impact, n.sentiment, n.minsBefore, n.minsAfter);
    else if (n.kind === "timeOfDay") nativeSeries[id] = timeOfDayArr(bars, n.tz, n.startMin, n.endMin);
    else if (n.kind === "dayOfWeek") nativeSeries[id] = dayOfWeekArr(bars, n.tz, n.days);
    else if (n.kind === "monthOfYear") nativeSeries[id] = monthOfYearArr(bars, n.tz, n.months);
  }
  for (let i = 0; i < bars.length; i++) {
    for (const id of order) {
      const n = graph.nodes[id]!;
      let v: number;
      switch (n.kind) {
        case "price": v = bars[i]![n.field]; break;
        case "const": v = n.value; break;
        case "sma": {
          if (i + 1 < n.period) { v = NaN; break; }
          let s = 0; for (let k = i - n.period + 1; k <= i; k++) s += at(n.in, k);
          v = s / n.period; break;
        }
        case "highest": {
          if (i + 1 < n.period) { v = NaN; break; }
          let m = -Infinity; for (let k = i - n.period + 1; k <= i; k++) m = Math.max(m, at(n.in, k));
          v = m; break;
        }
        case "lowest": {
          if (i + 1 < n.period) { v = NaN; break; }
          let m = Infinity; for (let k = i - n.period + 1; k <= i; k++) m = Math.min(m, at(n.in, k));
          v = m; break;
        }
        case "ema": {
          const s = (st[id] ??= { level: NaN }); const p = at(n.in, i); const kk = 2 / (n.period + 1);
          s.level = Number.isNaN(s.level as number) ? p : (s.level as number) + kk * (p - (s.level as number));
          v = s.level as number; break;
        }
        case "rsi": {
          if (i < n.period) { v = NaN; break; }
          let gain = 0, loss = 0;
          for (let k = i - n.period + 1; k <= i; k++) {
            const d = at(n.in, k) - at(n.in, k - 1);
            if (d >= 0) gain += d; else loss -= d;
          }
          const ag = gain / n.period, al = loss / n.period;
          v = al === 0 ? 100 : 100 - 100 / (1 + ag / al); break;
        }
        case "shift": v = i - n.k >= 0 ? at(n.in, i - n.k) : NaN; break;
        case "swingLowLevel": {
          const s = (st[id] ??= { level: NaN }); const c = i - n.span;
          if (c - n.span >= 0) { const lc = at(n.in, c); let piv = true; for (let k = c - n.span; k <= c + n.span; k++) if (at(n.in, k) < lc) { piv = false; break; } if (piv) s.level = lc; }
          v = s.level ?? NaN; break;
        }
        case "swingHighLevel": {
          const s = (st[id] ??= { level: NaN }); const c = i - n.span;
          if (c - n.span >= 0) { const hc = at(n.in, c); let piv = true; for (let k = c - n.span; k <= c + n.span; k++) if (at(n.in, k) > hc) { piv = false; break; } if (piv) s.level = hc; }
          v = s.level ?? NaN; break;
        }
        case "trendlineLow": {
          const s = (st[id] ??= { pts: [] }); const c = i - n.span;
          if (c - n.span >= 0) { const lc = bars[c]!.l; let piv = true; for (let k = c - n.span; k <= c + n.span; k++) if (bars[k]!.l < lc) { piv = false; break; } if (piv) { s.pts!.push({ i: c, v: lc }); if (s.pts!.length > 2) s.pts!.shift(); } }
          if (s.pts!.length === 2) { const a = s.pts![0]!, b2 = s.pts![1]!; const slope = (b2.v - a.v) / (b2.i - a.i); v = b2.v + slope * (i - b2.i); } else v = NaN;
          break;
        }
        case "sub": v = at(n.a, i) - at(n.b, i); break;
        case "add": v = at(n.a, i) + at(n.b, i); break;
        case "mul": v = at(n.a, i) * at(n.b, i); break;
        case "div": { const d = at(n.b, i); v = d !== 0 ? at(n.a, i) / d : NaN; break; }
        case "stdev": {
          if (i + 1 < n.period) { v = NaN; break; }
          let mean = 0; for (let k = i - n.period + 1; k <= i; k++) mean += at(n.in, k); mean /= n.period;
          let varr = 0; for (let k = i - n.period + 1; k <= i; k++) { const d = at(n.in, k) - mean; varr += d * d; } varr /= n.period;
          v = Math.sqrt(varr); break;
        }
        case "abs": v = Math.abs(at(n.in, i)); break;
        case "maxOf": v = Math.max(at(n.a, i), at(n.b, i)); break;
        case "minOf": v = Math.min(at(n.a, i), at(n.b, i)); break;
        case "atr": case "adx": case "vwap": case "newsWindow": case "newsEvent": case "timeOfDay": case "dayOfWeek": case "monthOfYear": v = nativeSeries[id]![i]!; break;
        case "gt": v = at(n.a, i) > at(n.b, i) ? 1 : 0; break;
        case "lt": v = at(n.a, i) < at(n.b, i) ? 1 : 0; break;
        case "crossAbove": {
          if (i === 0) { v = 0; break; }
          const upNow = at(n.a, i) > at(n.b, i);
          const downPrev = at(n.a, i - 1) <= at(n.b, i - 1);
          v = upNow && downPrev ? 1 : 0; break;
        }
        case "crossBelow": {
          if (i === 0) { v = 0; break; }
          const downNow = at(n.a, i) < at(n.b, i);
          const upPrev = at(n.a, i - 1) >= at(n.b, i - 1);
          v = downNow && upPrev ? 1 : 0; break;
        }
        case "and": v = at(n.a, i) && at(n.b, i) ? 1 : 0; break;
        case "or": v = at(n.a, i) || at(n.b, i) ? 1 : 0; break;
        case "not": v = at(n.a, i) ? 0 : 1; break;
        case "structureLatch": {
          const s = (st[id] ??= {}); const lvl = at(n.level, i); const bar = bars[i]!; const px = bar.c;
          let ev = 0;
          if (!Number.isNaN(lvl)) {
            if (!s.armed) {
              // ARM: "retest" on a decisive CLOSE beyond; "reclaim" on a wick SWEEP beyond.
              const armed = n.mode === "reclaim"
                ? (n.side === "below" ? bar.l < lvl : bar.h > lvl)
                : (n.side === "below" ? px < lvl : px > lvl);
              if (armed) { s.armed = true; s.armBar = i; s.lvl = lvl; }
            } else {
              const ref = s.lvl as number;
              const expired = i - (s.armBar as number) > n.windowBars;
              if (n.mode === "reclaim") {
                // FIRE on a CLOSE back on the original side (grab-fade reclaim).
                const reclaimBack = n.side === "below" ? px > ref : px < ref;
                if (reclaimBack) { ev = 1; s.armed = false; }
                else if (expired) { s.armed = false; }
              } else if (n.mode === "breakoutHold") {
                // FIRE when the break HOLDS: holdBars closed bars pass with no close
                // back through the level; a close back through before then = failed
                // breakout → reset (armed on the close-beyond, like retest).
                const brokeBack = n.side === "above" ? px < ref : px > ref;
                if (brokeBack) { s.armed = false; }
                else if (i - (s.armBar as number) >= (n.holdBars ?? 2)) { ev = 1; s.armed = false; }
              } else {
                const near = Math.abs(px - ref) <= ref * n.tol;               // retest within tol → FIRE
                const reclaimed = n.side === "below" ? px > ref : px < ref;    // structure repaired → reset
                if (near) { ev = 1; s.armed = false; }
                else if (reclaimed || expired) { s.armed = false; }
              }
            }
          }
          v = ev; break;
        }
        case "onTf": v = onTfSeries[id]![i]!; break;
      }
      hist[id]!.push(v);
    }
  }
  return hist;
}
